import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { color, font } from "../tokens";
import {
  DisplayTitle,
  Inner,
  MonoLabel,
  PillLink,
  PillButton,
  Reveal,
} from "../primitives";
import { CONTACT } from "../data";
import { useLenis } from "../LenisProvider";
import AnimatedIcon from "../icons";

const Wrap = styled.footer`
  background: ${color.ink};
  color: ${color.paper};
  padding: clamp(100px, 18vh, 200px) 0 40px;
`;

const CtaRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: clamp(80px, 14vh, 160px);
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 32px;
  padding-top: 32px;
  border-top: 1px solid ${color.lineOnInk};
  margin-bottom: clamp(60px, 10vh, 120px);
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLink = styled.a`
  font-family: ${font.mono};
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${color.paper};
  text-decoration: none;
  width: fit-content;
  transition: color 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover,
  &:focus-visible {
    color: ${color.accent};
  }
`;

const FooterRouteLink = styled(FooterLink).attrs({ as: Link })``;

const BottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const useSydneyTime = () => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const fmt = new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Sydney",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const tick = () => setTime(fmt.format(new Date()));
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, []);
  return time;
};

/** Section 8 — inverted contact/footer bookend. */
const FooterSection: React.FC = () => {
  const lenis = useLenis();
  const time = useSydneyTime();

  const backToTop = () => {
    if (lenis) {
      lenis.scrollTo(0);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <Wrap id="contact" data-ink>
      <Inner>
        <Reveal>
          <MonoLabel
            $onInk
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 20,
            }}
          >
            <AnimatedIcon name="send" size={14} strokeWidth={2} />
            Contact
          </MonoLabel>
          <CtaRow>
            <DisplayTitle $size="display" as="p">
              Say hi
            </DisplayTitle>
            <PillLink $solid href={`mailto:${CONTACT.email}`}>
              ↳ Email
            </PillLink>
          </CtaRow>
        </Reveal>
        <Columns>
          <Col>
            <MonoLabel $onInk>Elsewhere</MonoLabel>
            <FooterLink
              href={CONTACT.linkedIn}
              target="_blank"
              rel="noreferrer"
            >
              LinkedIn ↗
            </FooterLink>
            <FooterLink
              href="https://github.com/ElbertWan"
              target="_blank"
              rel="noreferrer"
            >
              GitHub ↗
            </FooterLink>
          </Col>
          <Col>
            <MonoLabel $onInk>On this site</MonoLabel>
            <FooterRouteLink to="/classic">Classic site</FooterRouteLink>
            <FooterRouteLink to="/showcase/firstrescue">
              First Rescue showcase
            </FooterRouteLink>
          </Col>
          <Col>
            <MonoLabel $onInk>Local time</MonoLabel>
            <MonoLabel $onInk as="span" style={{ color: color.paper }}>
              {time} SYD
            </MonoLabel>
            <PillButton
              $onInk
              onClick={backToTop}
              style={{ marginTop: 8, width: "fit-content" }}
            >
              Back to top ⇱
            </PillButton>
          </Col>
        </Columns>
        <BottomRow>
          <MonoLabel $onInk>©2026 Elbert Wan</MonoLabel>
          <MonoLabel $onInk>Sydney, Australia</MonoLabel>
        </BottomRow>
      </Inner>
    </Wrap>
  );
};

export default FooterSection;
